import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, useMediaQuery, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { brandLogoMap } from "../../assets/dummyData";
import { useAutoDragScroll } from "../../utils/useAutoDragScroll";
import { fetchHomepageBrandsShowcase } from "../../redux/slices/brandsShowcaseSlice";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const ExploreMoreBrands = ({ excludeBrand, hideTabs, defaultTab, title }) => {
  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { ref: scrollRefNew, node: nodeNew, triggerPause: pauseNew } = useAutoDragScroll({ speed: 0.5, pauseDelay: 2000 });
  const { ref: scrollRefSale, node: nodeSale, triggerPause: pauseSale } = useAutoDragScroll({ speed: 0.5, pauseDelay: 2000 });

  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(defaultTab || "new_arrivals");
  const { onSale, loading: reduxLoading } = useSelector((state) => state.brandsShowcase);

  useEffect(() => {
    if (Object.keys(onSale || {}).length === 0 && !reduxLoading) {
      dispatch(fetchHomepageBrandsShowcase());
    }
  }, [dispatch, onSale, reduxLoading]);

  const brandsWithSales = useMemo(() => {
    return Object.keys(onSale || {}).filter(
      (brandName) => onSale[brandName] && onSale[brandName].length > 0
    );
  }, [onSale]);

  const newArrivalBrandsBase = brandLogoMap.filter(brand => brand.name !== excludeBrand);
  const onSaleBrandsBase = brandLogoMap.filter(brand => brandsWithSales.includes(brand.name) && brand.name !== excludeBrand);

  // Triplicate for seamless bidirectional infinite scrolling
  const newArrivalBrands = [...newArrivalBrandsBase, ...newArrivalBrandsBase, ...newArrivalBrandsBase];
  const onSaleBrands = [...onSaleBrandsBase, ...onSaleBrandsBase, ...onSaleBrandsBase];

  if (reduxLoading) {
    return (
      <Box sx={{ maxWidth: "1200px", mx: "auto", px: smallScreen ? 2 : 3, py: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Skeleton variant="rectangular" width={240} height={45} sx={{ borderRadius: "100px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" width={150} height={60} sx={{ borderRadius: "8px" }} />
          ))}
        </Box>
      </Box>
    );
  }

  const renderBrandCard = (brand, type, index) => (
    <Box
      key={`${brand.name}-${index}`}
      onClick={() => {
        const path = type === "on_sale" ? "/on-sale" : "/new-arrivals";
        const slug = brand.name.replace(/\s+/g, '-');
        navigate(`${path}/${encodeURIComponent(slug)}`);
      }}
      sx={{
        minWidth: "150px",
        height: "60px",
        flexShrink: 0,
        position: "relative",
        cursor: "pointer",
        "&:hover .inner-card": {
          boxShadow: type === "new_arrivals"
            ? "0 12px 30px rgba(117, 214, 165, 0.25)"
            : "0 12px 30px rgba(255, 155, 128, 0.25)",
          transform: "translateY(-4px)",
          borderColor: type === "new_arrivals"
            ? "rgba(117, 214, 165, 0.4)"
            : "rgba(255, 155, 128, 0.4)",
        },
        "&:active .inner-card": {
          transform: "scale(0.96)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        },
      }}
    >
      <Box
        className="inner-card"
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "8px",
          border: "1px solid rgba(255,255,255,0.8)",
          background: "linear-gradient(145deg, #ffffff 0%, #f9f9fb 100%)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
          overflow: "hidden",
          padding: 1,
          boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
        }}
      >
        {brand.logo ? (
          <img
            src={brand.logo}
            alt={brand.name}
            style={{
              width: "100%",
              height: "100%",
              maxWidth: "135px",
              maxHeight: "45px",
              objectFit: "contain",
              transform: brand.scale ? `scale(${brand.scale})` : "none",
            }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: "Poppins-Bold",
              fontSize: "14px",
              color: "#222",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}
          >
            {brand.name}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        maxWidth: "1200px",
        mx: "auto",
        px: smallScreen ? 2 : 3,
        pt: smallScreen ? 2 : 2.5,
        pb: 4,
      }}
    >
      {/* Premium Pill Tabs with Sliding Indicator or Custom Title */}
      {!hideTabs ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: smallScreen ? 3.5 : 5,
            position: "relative",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              position: "relative",
              backgroundColor: "rgba(247, 248, 250, 0.8)",
              backdropFilter: "blur(12px)",
              p: 0.5,
              borderRadius: "100px",
              border: "1px solid rgba(255,255,255,0.6)",
              boxShadow: "inset 0 2px 6px rgba(0,0,0,0.02), 0 4px 15px rgba(0,0,0,0.03)",
            }}
          >
            {/* Sliding Background Pill */}
            <Box
              sx={{
                position: "absolute",
                top: "4px",
                bottom: "4px",
                left: "4px",
                width: "calc(50% - 4px)",
                backgroundColor: "#fff",
                borderRadius: "100px",
                boxShadow: activeTab === "new_arrivals"
                  ? "0 4px 16px rgba(255, 107, 0, 0.15)"
                  : "0 4px 16px rgba(255, 107, 0, 0.15)",
                transform: activeTab === "new_arrivals" ? "translateX(0)" : "translateX(100%)",
                transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                zIndex: 1,
              }}
            />

            <Box
              onClick={() => setActiveTab("new_arrivals")}
              sx={{
                px: { xs: 3, sm: 4 },
                py: 1,
                cursor: "pointer",
                position: "relative",
                zIndex: 2,
                flex: 1,
                textAlign: "center",
                minWidth: { xs: "130px", sm: "160px" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: activeTab === "new_arrivals" ? "Poppins-Bold" : "Poppins-SemiBold",
                  fontSize: { xs: "13px", sm: "14px" },
                  color: activeTab === "new_arrivals" ? "#FF6B00" : "#999",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  transition: "color 0.3s ease",
                  whiteSpace: "nowrap",
                }}
              >
                New Arrivals
              </Typography>
            </Box>
            <Box
              onClick={() => setActiveTab("on_sale")}
              sx={{
                px: { xs: 3, sm: 4 },
                py: 1,
                cursor: "pointer",
                position: "relative",
                zIndex: 2,
                flex: 1,
                textAlign: "center",
                minWidth: { xs: "130px", sm: "160px" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: activeTab === "on_sale" ? "Poppins-Bold" : "Poppins-SemiBold",
                  fontSize: { xs: "13px", sm: "14px" },
                  color: activeTab === "on_sale" ? "#FF6B00" : "#999",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  transition: "color 0.3s ease",
                  whiteSpace: "nowrap",
                }}
              >
                On Sale
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : null}

      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, flexDirection: { xs: "column", md: "row" }, width: "100%", gap: { xs: 1, md: 4 } }}>
        {hideTabs && (
          <Typography
            sx={{
              fontFamily: "Poppins-SemiBold",
              fontSize: { xs: "12px", md: "14px" },
              color: "#FF6B00",
              textTransform: "uppercase",
              letterSpacing: "1.5px",
              flexShrink: 0,
              whiteSpace: "nowrap"
            }}
          >
            Explore more
          </Typography>
        )}

        {/* Brand Tiles Carousels (Rendered conditionally to preserve individual scroll states) */}
        <Box sx={{ position: "relative", flexGrow: 1, minWidth: 0, width: "100%" }}>

        {/* Left Arrow Indicator */}
        <Box
          sx={{
            position: "absolute",
            left: { xs: -12, sm: -16 },
            top: "50%",
            transform: "translateY(-50%)",
            color: "rgba(255, 107, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            userSelect: "none",
            WebkitUserSelect: "none",
            zIndex: 1,
            opacity: 0.8,
            transition: "all 0.3s ease",
            "&:hover": {
              opacity: 1,
              transform: "translateY(-50%) scale(1.15)",
              color: "rgba(255, 107, 0, 1)",
            }
          }}
          onClick={() => {
            const activeNode = activeTab === "new_arrivals" ? nodeNew : nodeSale;
            const activePause = activeTab === "new_arrivals" ? pauseNew : pauseSale;
            if (activeNode) {
              activeNode.scrollBy({ left: -200, behavior: 'smooth' });
              if (activePause) activePause();
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </Box>

        {/* Right Arrow Indicator */}
        <Box
          sx={{
            position: "absolute",
            right: { xs: -12, sm: -16 },
            top: "50%",
            transform: "translateY(-50%)",
            color: "rgba(255, 107, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            userSelect: "none",
            WebkitUserSelect: "none",
            zIndex: 1,
            opacity: 0.8,
            transition: "all 0.3s ease",
            "&:hover": {
              opacity: 1,
              transform: "translateY(-50%) scale(1.15)",
              color: "rgba(255, 107, 0, 1)",
            }
          }}
          onClick={() => {
            const activeNode = activeTab === "new_arrivals" ? nodeNew : nodeSale;
            const activePause = activeTab === "new_arrivals" ? pauseNew : pauseSale;
            if (activeNode) {
              activeNode.scrollBy({ left: 200, behavior: 'smooth' });
              if (activePause) activePause();
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </Box>

        <Box sx={{
          position: "relative",
          borderRadius: "16px",
          border: "1px solid",
          borderColor: "rgba(255, 107, 0, 0.4)",
          boxShadow: "0 0 24px rgba(255, 107, 0, 0.12), inset 0 0 12px rgba(255, 107, 0, 0.04)",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          transition: "all 0.4s ease",
          overflow: "hidden",
        }}>
          <Box sx={{
            WebkitMaskImage: "linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)",
            maskImage: "linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)",
            width: "100%",
          }}>

            {/* New Arrivals Carousel */}
            <Box
              ref={scrollRefNew}
              className="flex overflow-x-auto hide-scrollbar"
              sx={{
                display: activeTab === "new_arrivals" ? "flex" : "none",
                gap: 2,
                px: 2,
                pt: 1, // Space for translateY(-4px)
                pb: 1, // Space for 12px hover shadow
                width: "100%",
                "&::-webkit-scrollbar": { display: "none" },
                cursor: "grab",
                userSelect: "none",
                WebkitUserSelect: "none",
                "&:active": { cursor: "grabbing" },
              }}
            >
              {newArrivalBrands.map((brand, index) => renderBrandCard(brand, "new_arrivals", index))}
            </Box>

            {/* On Sale Carousel */}
            <Box
              ref={scrollRefSale}
              className="flex overflow-x-auto hide-scrollbar"
              sx={{
                display: activeTab === "on_sale" ? "flex" : "none",
                gap: 2,
                px: 2,
                pt: 1,
                pb: 1,
                width: "100%",
                "&::-webkit-scrollbar": { display: "none" },
                cursor: "grab",
                userSelect: "none",
                WebkitUserSelect: "none",
                "&:active": { cursor: "grabbing" },
              }}
            >
              {onSaleBrands.length === 0 ? (
                <Typography sx={{ fontFamily: "Poppins-Regular", color: "#666", width: "100%", textAlign: "center", py: 2 }}>
                  No brands are currently on sale.
                </Typography>
              ) : (
                onSaleBrands.map((brand, index) => renderBrandCard(brand, "on_sale", index))
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      </Box>

      {/* View All Brands Link Bottom Right */}
      {hideTabs && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5, px: { xs: 2, sm: 3 }, maxWidth: "1200px", mx: "auto" }}>
          <Typography 
            onClick={() => navigate(activeTab === "on_sale" ? "/brands/on-sale" : "/brands/new-arrivals")}
            sx={{ 
              fontFamily: "Poppins-Medium", 
              fontSize: { xs: "12px", md: "14px" }, 
              color: "#FF6B00", 
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": { textDecoration: "underline" }
            }}
          >
            {activeTab === "on_sale" ? "View all On Sale" : "View all New Arrivals"} <ArrowForwardIosIcon sx={{ fontSize: "10px" }} />
          </Typography>
        </Box>
      )}

    </Box>
  );
};

export default ExploreMoreBrands;
